/** The `service-counter` family: Papa's-style order assembly, tap/click
 * first. A visitor walks up to the counter and opens an order ticket drawn
 * from a menu of courses; the player assembles it by tapping placed tools in
 * the ticket's exact order (some steps graded on a timing gauge); patience
 * drains while the visitor waits and sets the mood; a finished order tips by
 * accuracy and speed, a ruined one or a walk-out costs a strike. Both
 * templates are the same modules — `body.cursor`, `rule.pressable`,
 * `body.glide`, `ai.patron`, `rule.patience`, `rule.ticket`, `rule.gauge`,
 * `flow.director`, `flow.escalation`, `rule.resource` (the shift clock), `flow.grade`,
 * `flow.objective` — composed two ways, and every item is a placed tool's
 * DATA (item id, label, palette colour), so a content patch re-themes the
 * menu without touching a rule:
 *
 *  - `service-counter/scoop-rush` — authored from the stored Scoop Rush
 *    concept: a beachside sundae counter, a fixed queue of ten customers
 *    (every third a VIP: a longer topping list, less patience, double
 *    points), cup → flavour (stop the pour-meter in its green zone) → syrup
 *    (time the drizzle the same way) → toppings in exact ticket order →
 *    SERVE; a wrong flavour or topping ruins the order, three ruined orders
 *    or the shift clock running out loses, and the shift is won only with a
 *    cumulative rating of 70% or better.
 *  - `service-counter/glow-rush` — authored from the stored Glow Rush concept:
 *    a pop-up beauty salon, each picky client's request card lists four exact
 *    steps (cleanse, hair colour & style, nail shade, outfit & accessory) to
 *    click at the stations in order; a wrong tool or a skipped step does not
 *    advance, later clients have less patience, a perfect client carries
 *    bonus patience to the next, serving five in a row wins and any client
 *    walking out loses. */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { CourseDef, TicketConfig } from '../modules/order.js';
import type { PatienceConfig } from '../modules/patron.js';
import type { PhaseDef } from '../modules/director.js';
import type { TemplateV1 } from './template.js';

const W = 16, H = 9;
const SEC = 30;

/** A placed, tappable tool (world.layout entry, top-left rectangle). */
type Tool = { archetype: string; x: number; y: number; w: number; h: number; item: string; label: string; tint: number };

const tool = (archetype: string, cx: number, bottom: number, size: number, item: string, label: string, tint: number): Tool =>
  ({ archetype, x: cx - size / 2, y: bottom - size, w: size, h: size, item, label, tint });

const cursorKind = (role: string, lives: number, palette: number): GameSpecV1['archetypes'][string] => ({
  role, tags: ['player', 'cursor'], size: { w: 0.7, h: 0.7 }, render: { palette, depth: 30, anchor: 'center', facing: 'front' },
  modules: [
    { use: 'body.cursor', config: { speed: 12, pressTicks: 6 } },
    { use: 'core.vitals', config: { lives, hurtTicks: 30, respawnTicks: 0, invulnTicks: 0 } },
  ],
});

const visitor = (role: string, palette: number, size: { w: number; h: number }, seat: { x: number; y: number }, exit: { x: number; y: number },
  patience: PatienceConfig, ticket: TicketConfig): GameSpecV1['archetypes'][string] => ({
  role, tags: ['visitor', 'required'], size, render: { palette, depth: 10, anchor: 'bottom', facing: 'front' },
  modules: [
    { use: 'body.glide', config: { speed: 4.5 } },
    { use: 'ai.patron', config: { seats: [seat], exit, waitTag: 'waiting' } },
    { use: 'rule.patience', config: patience },
    { use: 'rule.ticket', config: ticket },
  ],
});

const shown = (role: string, palette: number, size: { w: number; h: number }, depth: number, neutral = false): GameSpecV1['archetypes'][string] => ({
  role, tags: [], size, render: { palette, depth, anchor: 'bottom', ...(neutral ? { neutral: true } : {}) }, modules: [{ use: 'body.static', config: {} }],
});

const tappable = (role: string, palette: number, size: number, extra: ModuleUseV1[] = [], pose = true): GameSpecV1['archetypes'][string] => ({
  role, tags: ['tool'], size: { w: size, h: size }, render: { palette, depth: 5, anchor: 'bottom', neutral: true, facing: 'front' },
  modules: [{ use: 'body.static', config: {} }, { use: 'rule.pressable', config: { cursorTag: 'cursor', pad: 0.12, pose } }, ...extra],
});

const visitorPhase = (archetype: string, from: { x: number; y: number }): PhaseDef => ({
  kind: 'clear', durationTicks: 0, clearBonus: 0, layout: [],
  spawns: [{ archetype, count: 1, via: 'point', x: from.x, y: from.y, dir: 1, delayTicks: 0, everyTicks: 0, jitterTicks: 0 }],
});

const director = (phases: PhaseDef[], interPhaseTicks: number): ModuleUseV1 => ({ use: 'flow.director',
  config: { phases, loopFrom: -1, interPhaseTicks, requiredTag: 'required', collectTag: 'bonus-item', sweepTag: 'bonus-item' } });

const objective = (count: number): ModuleUseV1 => ({ use: 'flow.objective',
  config: { win: 'phases-cleared', count, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'bonus-item', celebrate: true } });

// ── Scoop Rush ──────────────────────────────────────────────────────────────

/** Scoop Rush palette: each index keeps its role (see the template's kinds). */
const SCOOP_PALETTE = ['#ffd93b', '#ff8fab', '#4dc9e6', '#fff3e0', '#8b5e3c', '#ff5c5c', '#7ed957', '#ffffff',
  '#d9963b', '#a6e3f5', '#5b3a29', '#2b2d42'];
const SCOOP_ENTER = { x: -1.4, y: 7.6 }, SCOOP_SEAT = { x: 2.3, y: 7.6 };

/** The counter, left to right: cups | flavour tubs | syrup bottles on the top
 * row, the pour and drizzle meters under their stations, toppings and SERVE
 * on the lower row, the sundae taking shape beside the cups. */
const SCOOP_TOOLS: Tool[] = [
  tool('ingredient', 5.3, 2.6, 1, 'small', 'SMALL', 2), tool('ingredient', 6.45, 2.6, 1, 'medium', 'MEDIUM', 0), tool('ingredient', 7.6, 2.6, 1, 'large', 'LARGE', 1),
  tool('ingredient', 9.2, 2.6, 1, 'vanilla', 'VANILLA', 3), tool('ingredient', 10.35, 2.6, 1, 'chocolate', 'CHOCOLATE', 4), tool('ingredient', 11.5, 2.6, 1, 'strawberry', 'STRAWBERRY', 1),
  tool('ingredient', 13.1, 2.6, 1, 'caramel', 'CARAMEL', 8), tool('ingredient', 14.25, 2.6, 1, 'fudge-syrup', 'FUDGE', 10), tool('ingredient', 15.4, 2.6, 1, 'berry-syrup', 'BERRY', 5),
  tool('ingredient', 9.2, 6.4, 1, 'whip', 'WHIP', 7), tool('ingredient', 10.35, 6.4, 1, 'cherry', 'CHERRY', 5), tool('ingredient', 11.5, 6.4, 1, 'sprinkles', 'SPRINKLES', 1),
  tool('ingredient', 12.65, 6.4, 1, 'nuts', 'NUTS', 4), tool('ingredient', 13.8, 6.4, 1, 'choc-chips', 'CHOC CHIPS', 10),
  tool('ingredient', 15.2, 6.6, 1.25, 'serve', 'SERVE', 0),
];
const SCOOP_GAUGES = [
  { archetype: 'meter', x: 8.7, y: 3.25, w: 3.3, h: 0.75, item: 'pour', label: 'POUR' },
  { archetype: 'meter', x: 12.6, y: 3.25, w: 3.3, h: 0.75, item: 'drizzle', label: 'DRIZZLE' },
];

const scoopCourses = (toppings: { min: number; max: number }): CourseDef[] => [
  { id: 'cup', options: ['small', 'medium', 'large'], min: 1, max: 1, gauge: 'none', onWrong: 'accept', build: 'base' },
  { id: 'flavor', options: ['vanilla', 'chocolate', 'strawberry'], min: 1, max: 1, gauge: 'pour', onWrong: 'ruin', build: 'layer' },
  { id: 'syrup', options: ['caramel', 'fudge-syrup', 'berry-syrup'], min: 1, max: 1, gauge: 'drizzle', onWrong: 'accept', build: 'layer' },
  { id: 'topping', options: ['whip', 'cherry', 'sprinkles', 'nuts', 'choc-chips'], ...toppings, gauge: 'none', onWrong: 'ruin', build: 'layer' },
  { id: 'serve', options: ['serve'], min: 1, max: 1, gauge: 'none', onWrong: 'ignore', build: 'none' },
];

const scoopTicket = (vip: boolean): TicketConfig => ({
  courses: scoopCourses(vip ? { min: 4, max: 5 } : { min: 2, max: 4 }),
  waitTag: 'waiting', outOfOrder: 'ignore',
  card: { archetype: 'order-ticket', x: 2.3, y: 4.2, title: vip ? 'VIP ORDER' : 'ORDER' },
  dish: { archetype: 'sundae', x: 6.45, y: 8.3, layer: 'scoop', layerY: 7.0, layerStep: 0.32 },
  scoring: { accuracy: 0.7, speed: 0.3, tip: 20, weight: vip ? 2 : 1 },
  lifeOnFail: true,
});

const scoopPatience = (seconds: number): PatienceConfig => ({ ticks: seconds * SEC, scaleScalar: 'patience.scale', carryScalar: 'patience.carry',
  perfectBonusTicks: 0, waitTag: 'waiting', impatientBelow: 0.5, angryBelow: 0.25, meter: true });

/** The fixed queue of ten: every third customer is a VIP. */
export function scoopQueue(): PhaseDef[] {
  return Array.from({ length: 10 }, (_, i) => visitorPhase((i + 1) % 3 === 0 ? 'vip-customer' : 'customer', SCOOP_ENTER));
}

export const SCOOP_RUSH_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Scoop Rush',
  summary: 'a tap-to-serve sundae counter time-management game',
  world: { w: W, h: H, orientation: 'landscape' },
  players: { archetypes: ['server'], spawns: [{ x: 7.6, y: 8.6, facing: 1 }] },
  archetypes: {
    server: cursorKind('the server\'s open empty hand in a pastel glove, the player\'s pointer', 3, 0),
    customer: visitor('a cheerful empty-handed beachgoer in a sunhat and swimsuit', 2, { w: 2, h: 3 },
      SCOOP_SEAT, SCOOP_ENTER, scoopPatience(26), scoopTicket(false)),
    'vip-customer': visitor('a VIP beach customer in sunglasses and a gold visor, demanding and impatient', 0, { w: 2, h: 3 },
      SCOOP_SEAT, SCOOP_ENTER, scoopPatience(18), scoopTicket(true)),
    ingredient: tappable('a rounded counter ingredient bin, one plain shape recoloured per item', 3, 1),
    meter: { ...tappable('a horizontal pour/drizzle gauge track with rounded ends', 11, 0.75, [{ use: 'rule.gauge',
      config: { periodTicks: 64, zoneMin: 0.14, zoneMax: 0.26, falloff: 0.35, speedScalar: 'gauge.speed' } }], false), size: { w: 3.3, h: 0.75 } },
    'order-ticket': shown('a small paper order ticket, slight curl', 3, { w: 2.9, h: 3.5 }, 4),
    sundae: shown('an empty striped paper sundae cup, three-quarter view', 7, { w: 1.5, h: 1.5 }, 6, true),
    scoop: shown('a single rounded scoop of ice cream, one plain shape recoloured per flavour', 3, { w: 1.25, h: 0.42 }, 7, true),
  },
  globals: [
    { use: 'world.layout', config: { layout: [...SCOOP_TOOLS, ...SCOOP_GAUGES] } },
    director(scoopQueue(), 36),
    // Shift clock: the queue of ten must be cleared inside it.
    { use: 'rule.resource', key: 'shift', config: { label: 'SHIFT', display: 'seconds', capacity: 240 * SEC, drainPerTick: 1, startRunning: true,
      fillOn: [], adjust: [], cashOutOn: [], cashOutPoints: 0, onEmpty: 'lose' } },
    // The payout needs a cumulative rating of 70% once the tenth order is in.
    { use: 'flow.grade', config: { minRatio: 0.7, untilScalar: 'phase.cleared', untilCount: 10 } },
    objective(10),
  ],
  hud: [{ from: 'score', field: 'score', label: 'TIPS' }, { from: 'flow.director', field: 'phase', label: 'CUSTOMER' },
    { from: 'flow.grade', field: 'rating' }, { from: 'core.vitals', field: 'lives', label: 'CHANCES' }, { from: 'shift', field: 'level', label: 'SHIFT' }],
  input: { profile: 'pointer', labels: { a: 'TAP' } },
  presentation: {
    projection: 'front', backgroundRole: 'a sunny beachside ice-cream stand counter with a pastel tiled backsplash, ocean and palms through the window',
    palette: SCOOP_PALETTE, backgroundPaletteIndex: 9,
  },
};

export const SCOOP_RUSH_TEMPLATE: TemplateV1 = {
  id: 'service-counter/scoop-rush',
  family: 'service-counter',
  title: 'Scoop Rush sundae counter',
  pitch: 'Tap-to-serve counter rush: read each ticket, pour and drizzle on timing meters, stack toppings in order and serve ten customers before the shift ends.',
  disclosure: {
    coreVerbs: ['pick ingredients', 'stop the meter'],
    mechanics: [
      'Customers walk up one at a time with an order ticket: a cup size, a base flavour, a syrup and toppings in an exact sequence, then SERVE.',
      'Tap the ticket\'s items at the counter in order; the ticket ticks each step off and the sundae builds up as you go.',
      'After picking the flavour, a pour-meter sweeps back and forth: tap it while the needle is in the green zone. The syrup drizzle is timed the same way.',
      'Meter accuracy and speed set each order\'s quality and tip; a wrong cup or syrup is accepted but scores nothing for that step.',
      'A wrong flavour or a topping out of ticket order ruins the order.',
      'Each customer\'s patience shrinks while they wait (they get impatient, then angry); when it runs out they walk out and the order is ruined.',
      'Every third customer is a VIP: a longer topping sequence, less patience, double points.',
      'The shift is a fixed queue of ten customers on a shift clock.',
    ],
    controls: 'Tap or click the counter items, the meters and SERVE (arrow keys move the hand and Space taps on a keyboard).',
    winCondition: 'Serve all ten customers with a cumulative rating of 70% or better; three ruined orders or the shift clock running out loses.',
  },
  music: { mood: 'sunny playful frantic beach rush', genre: 'upbeat surf pop', tempoBpm: 132,
    instrumentation: ['surf guitar', 'ukulele', 'bouncy bass', 'hand claps'] },
  spec: SCOOP_RUSH_SPEC,
  tunables: [
    { path: '/archetypes/customer/modules/rule.patience/ticks', min: 18 * SEC, max: 40 * SEC, integer: true },
    { path: '/archetypes/vip-customer/modules/rule.patience/ticks', min: 12 * SEC, max: 30 * SEC, integer: true },
    { path: '/archetypes/meter/modules/rule.gauge/periodTicks', min: 40, max: 120, integer: true },
    { path: '/archetypes/meter/modules/rule.gauge/zoneMin', min: 0.1, max: 0.3 },
    { path: '/archetypes/meter/modules/rule.gauge/zoneMax', min: 0.18, max: 0.4 },
    { path: '/archetypes/server/modules/core.vitals/lives', min: 2, max: 5, integer: true },
    { path: '/globals/shift/capacity', min: 180 * SEC, max: 420 * SEC, integer: true },
    { path: '/globals/flow.grade/minRatio', min: 0.5, max: 0.85 },
    { path: '/globals/flow.director/interPhaseTicks', min: 15, max: 90, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
    { path: '/globals/world.layout/layout/*/label', maxLength: 12 },
  ],
  requirements: { events: ['arrive', 'press', 'step', 'gauge-hit', 'serve', 'leave', 'serve:vip-customer', 'won'] },
  signatureModules: ['rule.ticket', 'rule.pressable', 'body.cursor', 'ai.patron', 'rule.gauge'],
  // The first ingredient (a cup) and the pour-meter take a tap from the
  // opening of a fresh match; what a press then does needs an open order,
  // which the solver proof covers (step, gauge-hit and serve are required).
  verbProofs: [
    { verb: 'pick ingredients', input: 'tap', target: 'ingredient', ms: 300, expect: { kind: 'event', event: 'press' } },
    { verb: 'stop the meter', input: 'tap', target: 'meter', ms: 300, expect: { kind: 'event', event: 'press' } },
  ],
};

// ── Glow Rush ───────────────────────────────────────────────────────────────

const GLOW_PALETTE = ['#ff8fc4', '#ffd6e8', '#a06cff', '#ffe27a', '#5ad2c8', '#ffffff', '#3a2a4d', '#ff5a8a',
  '#a8432a', '#2b2233', '#ff6f61', '#b9a38f', '#4b6cff', '#fdf0f7'];
const GLOW_ENTER = { x: 17.6, y: 8.2 }, GLOW_SEAT = { x: 6.6, y: 8.2 };

/** Four station rows on the right: a station sign, then its tools. */
const GLOW_ROWS: Array<{ station: string; label: string; tint: number; tools: Array<[string, string, number]> }> = [
  { station: 'cleanse', label: 'CLEANSE', tint: 4, tools: [['foam-wash', 'FOAM WASH', 5], ['rose-toner', 'ROSE TONER', 1], ['clay-mask', 'CLAY MASK', 11]] },
  { station: 'hair', label: 'HAIR', tint: 2, tools: [['blonde-waves', 'BLONDE WAVES', 3], ['auburn-bob', 'AUBURN BOB', 8], ['jet-sleek', 'JET SLEEK', 9], ['pink-curls', 'PINK CURLS', 0]] },
  { station: 'nails', label: 'NAILS', tint: 7, tools: [['coral', 'CORAL', 10], ['lilac', 'LILAC', 2], ['ruby', 'RUBY', 7], ['mint', 'MINT', 4]] },
  { station: 'wardrobe', label: 'WARDROBE', tint: 12, tools: [['gown-tiara', 'GOWN+TIARA', 12], ['jumpsuit-hoops', 'SUIT+HOOPS', 6], ['sundress-hat', 'DRESS+HAT', 3], ['blazer-pearls', 'BLAZER+PEARL', 9]] },
];

function glowLayout(): Array<Tool | { archetype: string; x: number; y: number; w: number; h: number; label: string; tint: number }> {
  return GLOW_ROWS.flatMap((row, r) => {
    const bottom = 2.15 + r * 2.05;
    return [
      { archetype: 'station', x: 9.1, y: bottom - 1.3, w: 1.5, h: 1.3, label: row.label, tint: row.tint },
      ...row.tools.map(([item, label, tint], i) => tool('tool', 11.3 + i * 1.35, bottom, 1.15, item, label, tint)),
    ];
  });
}

const glowCourses = (): CourseDef[] => GLOW_ROWS.map(row => ({ id: row.station, options: row.tools.map(([item]) => item), min: 1, max: 1,
  gauge: 'none', onWrong: 'ignore', build: 'none' }));

export const GLOW_RUSH_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Glow Rush',
  summary: 'a click-the-right-tool beauty salon time-management game',
  world: { w: W, h: H, orientation: 'landscape' },
  players: { archetypes: ['stylist'], spawns: [{ x: 8.6, y: 8.6, facing: 1 }] },
  archetypes: {
    stylist: cursorKind('the stylist\'s open empty hand with painted nails, the player\'s pointer', 1, 0),
    client: visitor('a seated glam salon client in a cape, front view, hopeful and picky', 0, { w: 2.4, h: 3.6 }, GLOW_SEAT, GLOW_ENTER,
      { ticks: 14 * SEC, scaleScalar: 'patience.scale', carryScalar: 'patience.carry', perfectBonusTicks: 3 * SEC, waitTag: 'waiting',
        impatientBelow: 0.5, angryBelow: 0.25, meter: true },
      { courses: glowCourses(), waitTag: 'waiting', outOfOrder: 'ignore',
        card: { archetype: 'request-card', x: 2.3, y: 6.6, title: 'REQUEST CARD' },
        dish: { archetype: 'none', x: 0, y: 0, layer: 'none', layerY: 0, layerStep: 0 },
        scoring: { accuracy: 0.5, speed: 0.5, tip: 100, weight: 1 }, lifeOnFail: true }),
    tool: tappable('a rounded salon product bottle, one plain shape recoloured per choice', 5, 1.15),
    station: shown('a pastel salon station tray', 1, { w: 1.5, h: 1.3 }, 3, true),
    'request-card': shown('a blank pastel request card with four empty checkbox rows, slight paper curl', 5, { w: 3.8, h: 4.6 }, 4),
  },
  globals: [
    { use: 'world.layout', config: { layout: glowLayout() } },
    director(Array.from({ length: 5 }, () => visitorPhase('client', GLOW_ENTER)), 20),
    // Later clients drain faster: 14 s, then 1.4 s less per client served (floor 6 s).
    { use: 'flow.escalation', config: { curves: [{ scalar: 'patience.scale', index: 'phase.number', kind: 'linear', base: 1, rate: -0.1, min: 6 / 14, max: 1 }] } },
    objective(5),
  ],
  hud: [{ from: 'rule.ticket', field: 'served', label: 'STREAK' }, { from: 'flow.director', field: 'phase', label: 'CLIENT' },
    { from: 'score', field: 'score', label: 'TIPS' }],
  input: { profile: 'pointer', labels: { a: 'CLICK' } },
  presentation: {
    projection: 'front', backgroundRole: 'a front-facing pop-up beauty salon, pastel walls, fairy lights, a big mirror, tiled floor',
    palette: GLOW_PALETTE, backgroundPaletteIndex: 13,
  },
};

export const GLOW_RUSH_TEMPLATE: TemplateV1 = {
  id: 'service-counter/glow-rush',
  family: 'service-counter',
  title: 'Glow Rush salon checklist',
  pitch: 'Click-the-right-tool salon rush: follow each picky client\'s four-step request card in exact order before her patience runs out; five happy clients in a row wins.',
  disclosure: {
    coreVerbs: ['click station tools'],
    mechanics: [
      'Each client sits down with a request card listing four exact steps: cleanse her face, a hair colour and style, a nail shade, and an outfit with an accessory.',
      'Click the matching tool at each station in the card\'s order; the card ticks each step off.',
      'A wrong tool, or a station out of turn, does not advance the checklist.',
      'A patience meter drains while she waits; she gets impatient, then angry.',
      'Completing all four steps sends her off happy and the next client sits down; later clients have less patience.',
      'A client served with no wrong clicks gives the next client bonus patience.',
    ],
    controls: 'Click or tap the station tools (arrow keys move the hand and Space clicks on a keyboard).',
    winCondition: 'Serve five clients in a row to win; if any client\'s patience runs out, the rush is lost.',
  },
  music: { mood: 'bubbly upbeat glam rush hour', genre: 'bubblegum electro pop', tempoBpm: 124,
    instrumentation: ['synth plucks', 'bright pads', 'electro bass', 'finger snaps'] },
  spec: GLOW_RUSH_SPEC,
  tunables: [
    { path: '/archetypes/client/modules/rule.patience/ticks', min: 10 * SEC, max: 24 * SEC, integer: true },
    { path: '/archetypes/client/modules/rule.patience/perfectBonusTicks', min: 0, max: 6 * SEC, integer: true },
    { path: '/globals/flow.escalation/curves/0/rate', min: -0.15, max: 0 },
    { path: '/globals/flow.objective/count', min: 3, max: 5, integer: true },
    { path: '/globals/flow.director/interPhaseTicks', min: 10, max: 60, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
    { path: '/globals/world.layout/layout/*/label', maxLength: 12 },
  ],
  requirements: { events: ['arrive', 'press', 'step', 'serve', 'leave', 'won'] },
  signatureModules: ['rule.ticket', 'rule.pressable', 'body.cursor', 'ai.patron'],
  verbProofs: [
    { verb: 'click station tools', input: 'tap', target: 'tool', ms: 300, expect: { kind: 'event', event: 'press' } },
  ],
};
